import React, { useEffect, useState } from "react";
import EmissionCard from "../../components/Fleet/FleetEmission/EmissionCard";
import FuelParameterChart from "../../components/Fleet/FleetEmission/FuelParameterChart";
import EmissionOverviewChart from "../../components/Fleet/FleetEmission/EmissionOverviewChart";
import KPITrendsChart from "../../components/Fleet/FleetEmission/KPITrendsChart";
import {
  Cloud,
  Zap,
  Flame,
  Wind,
  Factory,
  Atom, Fuel, Rocket
} from "lucide-react";
import FleetHeader from "../../components/Fleet/FleetHeader";
import CollapsibleSection from "../../components/Common/CollapsibleSection";
import { format, subDays } from "date-fns";
import { fetchFleets } from "../../services/fleetService";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { getFleetEmissionSummary, getFleetEmissionKPITrends, getFleetEmissionOverview, getFleetFuelConsumptionParams } from "../../services/fleetEmissionService";
import DateRangePicker from "../../components/Common/DateRangePicker";

const FleetEmission = () => {
  const [fleets, setFleets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFleet, setCurrentFleet] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 7), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
  });
  const [emissionSummary, setEmissionSummary] = useState(null);
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [kpiTrends, setKpiTrends] = useState(null);
  const [emissionOverview, setEmissionOverview] = useState(null);
  const [emissionFuelParams, setEmissionFuelParams] = useState(null);

  console.log("dateRange", dateRange);
   console.log("FleetEmission - currentFleet changed:", currentFleet);
  const emissionTypes = {
    co2: {
      label: "Carbon Dioxide",
      symbol: "CO₂",
      color: "#34d399",
      icon: () => <Cloud className="w-5 h-5" />,
    },
    ch4: {
      label: "Methane",
      symbol: "CH₄",
      color: "#60a5fa",
      icon: () => <Fuel className="w-5 h-5" />,
    },
    n20: {
      label: "Nitrous oxide",
      symbol: "N₂O",
      color: "#f87171",
      icon: () => <Rocket className="w-5 h-5" />,
    },
    co2equivalent: {
      label: "Carbon Dioxide Equivalent",
      symbol: "CO₂ Equivalent",
      color: "#fbbf24",
      icon: () => <Cloud className="w-5 h-5" />,
    },
    ghg: {
      label: "Greenhouse Gas Emission",
      symbol: "GHG Intensity",
      color: "#34d399",
      icon: () => <Atom className="w-5 h-5" />,
    },
    sox: {
      label: "Sulfur Oxides",
      symbol: "SOₓ",
      color: "#60a5fa",
      icon: () => <Flame className="w-5 h-5" />,
    },
    nox: {
      label: "Nitrogen Oxides",
      symbol: "NOₓ",
      color: "#f87171",
      icon: () => <Zap className="w-5 h-5" />,
    },
    pm: {
      label: "Particulate Matter",
      symbol: "PM",
      color: "#fbbf24",
      icon: () => <Wind className="w-5 h-5" />,
    },
  };

    useEffect(() => {
      const fetchFleetsData = async () => {
        setLoading(true);
        try {
          const res = await fetchFleets();
          if (res.data.status === "success") {
            setFleets(res.data.data);
            if (res.data.data.length > 0) {
              // setCurrentFleet(res.data.data[0]);
              setCurrentFleet("all");
            }
          } else {
            toast.error("Failed to load fleets");
          }
        } catch (err) {
          toast.error("Error loading fleets");
        } finally {
          setLoading(false);
        }
      };
      fetchFleetsData();
    }, []);

  useEffect(() => {
    // if (!currentFleet || !currentFleet.fleetId) return;
    if (!currentFleet) return;
    if (!startDate || !endDate) return;

    const fetchEmissionSummary = async () => {
      setLoading(true);
      try {
        let params = {};
        if(currentFleet === "all"){
          params = {
            fleetId: 0,
            startDate: startDate,
            endDate: endDate
          }
        }else{
          params = {
            fleetId: currentFleet.fleetId,
            startDate: startDate,
            endDate: endDate,
          };
        }

        const res = await getFleetEmissionSummary(params);

        if (res.data.status === "success") {
          console.log("res.data.data:", res.data.data);
          setEmissionSummary(res.data.data);
        } else {
          toast.error("Failed to load emission summary");
          setEmissionSummary(null);
        }
      } catch (err) {
        toast.error("Error fetching emission summary");
        setEmissionSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionSummary();
  }, [currentFleet, startDate, endDate]);

  useEffect(() => {
    // if (!currentFleet || !currentFleet.fleetId) return;
    if (!currentFleet) return;
    if (!startDate || !endDate) return;

    const fetchKpiTrends = async () => {
      setLoading(true);
      try {
        let params = {};
        if(currentFleet === "all"){
          params = {
            fleetId: 0,
            startDate: startDate,
            endDate: endDate
          }
        }else{
          params = {
            fleetId: currentFleet.fleetId,
            startDate: startDate,
            endDate: endDate,
          };
        }

        const res = await getFleetEmissionKPITrends(params);

        if (res.data.status === "success") {
          setKpiTrends(res.data.data);
        } else {
          toast.error("Failed to load KPI trends");
          setKpiTrends(null);
        }
      } catch (err) {
        toast.error("Error fetching KPI trends");
        setKpiTrends(null);
      } finally {
        setLoading(false);
      }
    };

    fetchKpiTrends();
  }, [currentFleet, startDate, endDate]);

  useEffect(() => {
    // if (!currentFleet || !currentFleet.fleetId) return;
    if (!currentFleet) return;
    if (!startDate || !endDate) return;

    const fetchEmissionOverview = async () => {
      setLoading(true);
      try {
        let params = {};
        if(currentFleet === "all"){
          params = {
            fleetId: 0,
            startDate: startDate,
            endDate: endDate
          }
        }else{
          params = {
            fleetId: currentFleet.fleetId,
            startDate: startDate,
            endDate: endDate,
          };
        }

        const res = await getFleetEmissionOverview(params);
        console.log("EmissionOverviewResponse:", res);

        if (res.data.status === "success") {
          setEmissionOverview(res.data.data);
        } else {
          toast.error("Failed to load KPI trends");
          setEmissionOverview(null);
        }
      } catch (err) {
        toast.error("Error fetching KPI trends");
        setEmissionOverview(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionOverview();
  }, [currentFleet, startDate, endDate]);

  useEffect(() => {
    // if (!currentFleet || !currentFleet.fleetId) return;
    if (!currentFleet) return;
    if (!startDate || !endDate) return;

    const fetchFleetEmissionFuelParams = async () => {
      setLoading(true);
      try {
        let params = {};
        if(currentFleet === "all"){
          params = {
            fleetId: 0,
            startDate: startDate,
            endDate: endDate
          }
        }else{
          params = {
            fleetId: currentFleet.fleetId,
            startDate: startDate,
            endDate: endDate,
          };
        }

        const res = await getFleetFuelConsumptionParams(params);
        console.log("EmissionFuelConsResponse:", res);

        if (res.data.status === "success") {
          setEmissionFuelParams(res.data.data);
        } else {
          toast.error("Failed to load KPI trends");
          setEmissionFuelParams(null);
        }
      } catch (err) {
        toast.error("Error fetching KPI trends");
        setEmissionFuelParams(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFleetEmissionFuelParams();
  }, [currentFleet, startDate, endDate]);

  return (
    <div className="">
      <div className="w-full mx-auto">
        <div className="flex justify-between">
          <FleetHeader
            currentFleet={currentFleet}
            fleets={fleets}
            onFleetChange={setCurrentFleet}
            pageTitle="Emission Analytics"
          />

          {/* Date Range Picker */}
          <div className="">
            <DateRangePicker />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-6 mb-10">
          {Object.entries(emissionTypes).map(([key, meta]) => (
            <EmissionCard
              type={key}
              data={emissionSummary}
              emissionTypes={emissionTypes}
            />
          ))}
        </div>

        {/* Main Chart Section */}
        <div className="space-y-8">
          {/* Emission Overview Section */}
          <CollapsibleSection
            title="Fleet Emission Overview"
            icon={Factory}
            defaultCollapsed={false}
          >
            <EmissionOverviewChart emissionOverviewData={emissionOverview} dateRange={dateRange} />
          </CollapsibleSection>

          {/* Two-Column Charts Section */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> */}
          <div className="grid grid-cols-1">
            {/* Fuel Parameter Chart */}
            <CollapsibleSection
              title="Fuel Consumption Parameter"
              icon={Flame}
              iconColor="text-orange-500"
              defaultCollapsed={false}
            >
              <FuelParameterChart emissionFuelParams={emissionFuelParams} dateRange={dateRange} />
            </CollapsibleSection>

            {/* Ship State Chart */}
            {/* <CollapsibleSection
              title="Fleet Operational States"
              icon={Ship}
              iconColor="text-blue-500"
              defaultCollapsed={false}
            >
              <ShipLoadingState dateRange={dateRange} />
            </CollapsibleSection> */}
          </div>

          <div className="grid grid-cols-1">
            {/* Fuel Parameter Chart */}
            <CollapsibleSection
              title="KPI Trends"
              icon={Flame}
              iconColor="text-orange-500"
              defaultCollapsed={false}
            >
              <KPITrendsChart kpiTrends={kpiTrends} dateRange={dateRange} />
            </CollapsibleSection>
          </div>

          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8"> */}
          {/* Fuel Parameter Chart */}
          {/* <CollapsibleSection
              title="Fleet State Transition"
              icon={Flame}
              iconColor="text-orange-500"
              defaultCollapsed={false}
            >
              <FleetStateTransition dateRange={dateRange} />
            </CollapsibleSection> */}

          {/* Ship State Chart */}
          {/* <CollapsibleSection
              title="Fuel Consumption per Consumer"
              icon={Ship}
              iconColor="text-blue-500"
              defaultCollapsed={false}
            >
              <FuelConsChart />
            </CollapsibleSection> */}
          {/* </div> */}
        </div>
      </div>
    </div>
  );
};

export default FleetEmission;
